import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

// Dos cosas que trae la referencia y el cliente quitó del hero: los botones
// "Conoce la firma" / "Nuestros servicios", y el logotipo, que ya está en el
// header justo encima.
export default function HeroSection() {
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const subtitleRef = useRef(null);
  const descRef = useRef(null);
  const lineRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;
    const heading = headingRef.current;
    const subtitle = subtitleRef.current;
    const desc = descRef.current;
    const line = lineRef.current;
    const scroll = scrollRef.current;
    if (!section || !heading || !subtitle || !desc || !line || !scroll) return;

    const capas = [heading, subtitle, desc];
    let parallax;

    // DESVIACIÓN RESPECTO A LA REFERENCIA — corrección de un defecto.
    // Allí el parallax con `scrub` se crea a la vez que la entrada. Como los
    // `.from()` ya han dejado los elementos en opacity:0, el tween con scrub
    // captura ESE 0 como valor de reposo y lo reimprime en cada update, así que
    // el hero se queda en blanco hasta que haces scroll (se ve en el propio
    // full-page-screenshot.png del zip: el hero sale vacío). Aquí el parallax
    // se crea cuando la entrada ya terminó, con los elementos en su estado
    // final, que es el reposo correcto.
    const tl = gsap.timeline({
      delay: 0.3,
      onComplete: () => {
        parallax = gsap.to(capas, {
          y: -60,
          opacity: 0.3,
          ease: 'none',
          scrollTrigger: {
            trigger: section,
            start: 'top top',
            end: 'bottom top',
            scrub: 0.5,
          },
        });
      },
    });

    // fromTo y no from, aunque la referencia use from. Motivo: si un elemento
    // lleva una transición CSS sobre la misma propiedad que anima GSAP, un
    // `.from()` escribe opacity:0 inline, eso dispara la transición CSS, y
    // cuando GSAP lee el valor de destino —lo hace de forma diferida, no al
    // crear el tween— se encuentra la opacidad a medio camino hacia 0 y la toma
    // como destino. Así se quedaban invisibles el logo (por el
    // `img { transition: opacity 0.3s }` de index.css) y los CTA (por su
    // `transition-all` de Tailwind), ambos ya retirados del hero. Con fromTo el
    // destino es explícito y no depende de leer el DOM en el momento justo.
    tl.fromTo(line, { scaleX: 0 }, { scaleX: 1, duration: 0.8, ease: 'expo.out' })
      .fromTo(heading, { opacity: 0, y: 40 }, { opacity: 1, y: 0, duration: 1, ease: 'expo.out' }, '-=0.5')
      .fromTo(subtitle, { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 0.8, ease: 'expo.out' }, '-=0.6')
      .fromTo(desc, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.8, ease: 'expo.out' }, '-=0.5')
      .fromTo(scroll, { opacity: 0 }, { opacity: 1, duration: 0.6, ease: 'power2.out' }, '-=0.2');

    return () => {
      tl.kill();
      parallax?.scrollTrigger?.kill();
      parallax?.kill();
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      id="hero"
      className="relative w-full min-h-[100dvh] flex items-center justify-center overflow-hidden"
      style={{
        background: 'linear-gradient(160deg, #0a0e14 0%, #141c24 40%, #1a1410 100%)',
      }}
    >
      {/* Subtle radial glow */}
      <div
        className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(201,162,110,0.06) 0%, transparent 70%)',
        }}
      />

      {/* Content */}
      <div className="relative z-10 text-center px-6 max-w-[900px] mx-auto">
        {/* Gold line */}
        <div
          ref={lineRef}
          className="w-16 h-px bg-[#C9A26E] mx-auto mb-10 origin-center"
        />

        {/* Eyebrow */}
        <p
          ref={subtitleRef}
          className="font-mono text-[11px] md:text-[12px] uppercase tracking-[0.2em] text-[#C9A26E]/80 mb-6"
        >
          Gobierno Corporativo para Empresas Familiares
        </p>

        {/* Main Heading */}
        <h1
          ref={headingRef}
          className="font-heading text-[#f0ebe2] text-[clamp(40px,7vw,90px)] leading-[0.95] tracking-[-0.02em] font-normal mb-8"
        >
          El legado familiar
          <br />
          <span className="text-[#8a8075]">no se improvisa.</span>
        </h1>

        {/* Description */}
        <p
          ref={descRef}
          className="font-body text-[#a09888] text-base md:text-lg font-light leading-relaxed max-w-[520px] mx-auto"
        >
          Acompañamos a familias empresarias en la construcción de gobierno,
          continuidad y trascendencia.
        </p>
      </div>

      {/* Scroll indicator */}
      <div
        ref={scrollRef}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#5a544c]">Scroll</span>
        <div className="w-px h-8 bg-gradient-to-b from-[#C9A26E]/40 to-transparent animate-pulse" />
      </div>
    </section>
  );
}
